import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCommunitySquads, type PublicSquadItem } from "@/features/public/api";

export default function SquadsSection() {
  const [visible, setVisible] = useState(false);
  const [squads, setSquads] = useState<PublicSquadItem[]>([]);
  const [loadingSquads, setLoadingSquads] = useState(true);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let isMounted = true;

    void (async () => {
      setLoadingSquads(true);

      try {
        const payload = await fetchCommunitySquads();

        if (!isMounted) {
          return;
        }

        setSquads(payload.slice(0, 3));
      } catch {
        if (!isMounted) {
          return;
        }

        setSquads([]);
      } finally {
        if (isMounted) {
          setLoadingSquads(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const getSportVisual = (sport: string) => {
    const normalized = sport.toLowerCase();
    if (normalized.includes("basket")) {
      return { symbol: "🏀", badgeClassName: "bg-[#fff4ea]" };
    }
    if (normalized.includes("tennis")) {
      return { symbol: "🏓", badgeClassName: "bg-[#fff3f7]" };
    }
    if (normalized.includes("volley")) {
      return { symbol: "🏐", badgeClassName: "bg-[#f5f8ff]" };
    }
    if (normalized.includes("course") || normalized.includes("athl")) {
      return { symbol: "🏃", badgeClassName: "bg-[#f2fbf4]" };
    }
    return { symbol: "⚽", badgeClassName: "bg-[#f5f5f5]" };
  };

  const getMembersBadgeClassName = (status: string) =>
    status === "complet" ? "bg-[#f3f4f6] text-[#5b6470]" : "bg-[#e9fbf6] text-[#0b9b82]";

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(620px,1.05fr)] lg:items-center">
          {/* Left */}
          <div className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 text-[#1A8C7A] text-sm font-medium mb-4">
              Communauté
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6">
              Créez votre squad
            </h2>
            <p className="text-lg text-gray-500 leading-relaxed mb-8">
              Un squad, c&apos;est une petite équipe solidaire pour s&apos;entraîner, se motiver et progresser ensemble.
              Idéal pour relever des défis et participer aux événements du club.
            </p>
            <Link
              to="/communaute/squads"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#E8521A] text-white font-semibold hover:bg-[#d44a16] transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Créer un squad
            </Link>
          </div>

          {/* Right - Squad Cards */}
          <div className={`space-y-5 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            {loadingSquads ? (
              Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="h-[168px] rounded-[28px] border border-[#e6e6e6] bg-white animate-pulse"
                ></div>
              ))
            ) : squads.length > 0 ? (
              squads.map((squad) => {
                const sportVisual = getSportVisual(squad.category);
                const isFull = squad.status === "FULL" || squad.members.length >= squad.maxMembers;

                return (
                  <Link
                    key={squad.id}
                    to="/communaute/squads"
                    className="group block rounded-[28px] border border-[#e6e6e6] bg-white px-5 py-5 shadow-[0_10px_28px_rgba(15,23,42,0.05)] hover:-translate-y-1 hover:border-[#d6d6d6] hover:shadow-[0_14px_30px_rgba(15,23,42,0.08)] transition-all duration-300"
                  >
                    <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_220px] md:gap-6">
                      <div className="min-w-0">
                        <div className="min-w-0">
                          <div className="text-[1.7rem] font-bold leading-tight text-[#1A1A1A] transition-colors group-hover:text-[#111111]">
                            {squad.name}
                          </div>
                          <div className="mt-1 text-lg leading-8 text-[#666666] line-clamp-2">{squad.description}</div>
                        </div>

                        <div className="mt-5 flex flex-wrap items-center gap-3">
                          <div className="flex items-center">
                            {Array.from({ length: 4 }).map((_, i) => {
                              const member = squad.members[i];
                              const zIndex = 4 - i;

                              return (
                                <div
                                  key={member?.id ?? `placeholder-${squad.id}-${i}`}
                                  className={`h-9 w-9 overflow-hidden rounded-full border-2 border-white flex-shrink-0 shadow-sm ${
                                    member ? "" : "bg-[#f1f2f4]"
                                  }`}
                                  style={{ marginLeft: i > 0 ? "-8px" : "0", zIndex }}
                                >
                                  {member?.avatar ? (
                                    <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center text-[#8e95a1]">
                                      <i className="ri-user-line text-sm"></i>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <span
                            className={`rounded-full px-3 py-1 text-sm font-semibold ${getMembersBadgeClassName(isFull ? "complet" : "ouvert")}`}
                          >
                            {squad.members.length}/{squad.maxMembers} membres
                          </span>
                        </div>
                      </div>

                      <div className="md:flex md:h-full md:items-center md:justify-center md:border-l md:border-[#ededed] md:pl-6">
                        <span className="inline-flex items-center gap-2 rounded-full border border-[#ececec] bg-[#fbfbfb] px-4 py-3 text-base font-semibold text-[#232323] shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                          <span
                            aria-hidden="true"
                            className={`flex h-9 w-9 items-center justify-center rounded-full text-[1.2rem] leading-none shadow-[0_4px_10px_rgba(15,23,42,0.08)] ${sportVisual.badgeClassName}`}
                          >
                            {sportVisual.symbol}
                          </span>
                          {squad.category}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })
            ) : (
              <div className="rounded-[28px] border border-dashed border-[#d9d9d9] bg-[#fafafa] px-6 py-10 text-center text-[#666666]">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-[#E8521A] shadow-sm">
                  <i className="ri-team-line text-2xl"></i>
                </div>
                <p className="mt-4 text-lg font-semibold text-[#1A1A1A]">Aucun squad validé pour le moment</p>
                <p className="mt-2 text-base">Dès qu&apos;un squad sera validé par l&apos;admin, il apparaîtra ici.</p>
              </div>
            )}

            <Link
              to="/communaute/squads"
              className="flex w-full items-center justify-center gap-2 rounded-full border border-[#1a1a1a] bg-white py-4 text-base font-semibold text-[#1a1a1a] transition-all duration-200 hover:bg-[#f8f8f8] cursor-pointer"
            >
              <i className="ri-eye-line"></i>
              Voir tous les squads
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
